import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Spinner } from "@/components/common";
import InstallAppButton from "@/components/InstallAppButton";
import AppErrorBoundary from "@/components/ErrorBoundary";
import VersionGate from "@/components/VersionGate";
import { setPwaContext, pwaKeyForPath } from "@/lib/pwa";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import PricingPublic from "@/pages/PricingPublic";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Clients from "@/pages/admin/Clients";
import ClientCreate from "@/pages/admin/ClientCreate";
import ClientDetail from "@/pages/admin/ClientDetail";
import Websites from "@/pages/admin/Websites";
import Leads from "@/pages/admin/Leads";
import LeadDetail from "@/pages/admin/LeadDetail";
import Tasks from "@/pages/admin/Tasks";
import TaskDetail from "@/pages/admin/TaskDetail";
import Support from "@/pages/admin/Support";
import Invoices from "@/pages/admin/Invoices";
import Modules from "@/pages/admin/Modules";
import Reports from "@/pages/admin/Reports";
import NextSteps from "@/pages/admin/NextSteps";
import QA from "@/pages/admin/QA";
import SystemData from "@/pages/admin/SystemData";
import SpecialChecks from "@/pages/admin/SpecialChecks";
import Settings from "@/pages/admin/Settings";
import DesignLibrary from "@/pages/admin/DesignLibrary";

import CockpitLayout from "@/pages/cockpit/CockpitLayout";
import Overview from "@/pages/cockpit/Overview";
import CLeads from "@/pages/cockpit/CLeads";
import CWebsite from "@/pages/cockpit/CWebsite";
import CTasks from "@/pages/cockpit/CTasks";
import CSupport from "@/pages/cockpit/CSupport";
import CPerformance from "@/pages/cockpit/CPerformance";
import CNextSteps from "@/pages/cockpit/CNextSteps";
import CReports from "@/pages/cockpit/CReports";
import CReviews from "@/pages/cockpit/CReviews";
import CDesign from "@/pages/cockpit/CDesign";
import CRequests from "@/pages/cockpit/CRequests";
import CFiles from "@/pages/cockpit/CFiles";
import AiRequests from "@/pages/admin/AiRequests";
import CDocsInvoices from "@/pages/cockpit/CDocsInvoices";
import CTeam from "@/pages/cockpit/CTeam";
import CSecurity from "@/pages/cockpit/CSecurity";
import AuditLog from "@/pages/admin/AuditLog";
import Security from "@/pages/admin/Security";
import Backup from "@/pages/admin/Backup";
import NoAccess from "@/pages/admin/NoAccess";
import AdminToday from "@/pages/admin/AdminToday";
import MySecurity from "@/pages/admin/MySecurity";
import WPGuard from "@/pages/admin/website-production/WPGuard";
import PCProduction from "@/pages/admin/pc-production/PCProduction";
import WPClientPackage from "@/pages/admin/website-production/WPClientPackage";
import WPList from "@/pages/admin/website-production/WPList";
import WPOrderForm from "@/pages/admin/website-production/WPOrderForm";
import WPOrderDetail from "@/pages/admin/website-production/WPOrderDetail";
import WPPresets from "@/pages/admin/website-production/WPPresets";
import AdminCockpitGate from "@/pages/admin/AdminCockpitGate";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";

const CLIENT_ROLES = ["client", "client_staff"];
const homeFor = (role) => (CLIENT_ROLES.includes(role) ? "/cockpit" : "/admin");

// Phase-2 RBAC: schuetzt kritische Bereiche im Frontend. Super Admin + Flag-AUS = immer Durchlass.
function SectionGuard({ section, children }) {
  const { user } = useAuth();
  if (!user || user.role === "super_admin") return children;
  if (!user.rbac_hard_enforce) return children;
  if (!(user.critical_sections || []).includes(section)) return children;
  if ((user.admin_sections || []).includes(section)) return children;
  return <NoAccess />;
}

// Setzt je nach Bereich das passende PWA-Manifest (Control OS vs. Website-Cockpit).
function PwaManifestSync() {
  const location = useLocation();
  React.useEffect(() => {
    const key = pwaKeyForPath(location.pathname);
    if (key) setPwaContext(key);
  }, [location.pathname]);
  return null;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AdminCockpitHome() {
  const { clientId } = useParams();
  return <Navigate to={`/admin/cockpit/${clientId}`} replace />;
}

function Protected({ roles, loginPath = "/login", children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to={loginPath} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login variant="cockpit" />} />
      <Route path="/admin/login" element={<Login variant="admin" />} />
      <Route path="/cockpit/login" element={<Login variant="cockpit" />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/preise" element={<PricingPublic />} />

      <Route path="/admin" element={<Protected roles={["super_admin", "team_member"]} loginPath="/admin/login"><AdminLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="today" element={<AdminToday />} />
        <Route path="cockpit" element={<AdminCockpitGate />} />
        <Route path="contacts" element={<AdminPlaceholder title="Kontakte" />} />
        <Route path="approvals" element={<Tasks approvalsOnly />} />
        <Route path="files" element={<AdminPlaceholder title="Dateien & Freigaben" />} />
        <Route path="performance" element={<AdminPlaceholder title="Besuche & Performance" />} />
        <Route path="ideas" element={<AdminPlaceholder title="Ideen & Verbesserungen" />} />
        <Route path="reviews" element={<AdminPlaceholder title="Bewertungen" />} />
        <Route path="subscriptions" element={<AdminPlaceholder title="Abos & Pakete" />} />
        <Route path="payments" element={<AdminPlaceholder title="Zahlungen" />} />
        <Route path="extras" element={<AdminPlaceholder title="Zusatzleistungen" />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/new" element={<ClientCreate />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="websites" element={<Websites />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="tasks/:id" element={<TaskDetail />} />
        <Route path="support" element={<Support />} />
        <Route path="ai-requests" element={<AiRequests />} />
        <Route path="invoices" element={<SectionGuard section="invoices"><Invoices /></SectionGuard>} />
        <Route path="modules" element={<Modules />} />
        <Route path="design-library" element={<DesignLibrary />} />
        <Route path="reports" element={<Reports />} />
        <Route path="next-steps" element={<NextSteps />} />
        <Route path="qa" element={<SectionGuard section="qa"><QA /></SectionGuard>} />
        <Route path="system" element={<SectionGuard section="qa"><SystemData /></SectionGuard>} />
        <Route path="special" element={<SectionGuard section="qa"><SpecialChecks /></SectionGuard>} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="my-security" element={<MySecurity />} />
        <Route path="security" element={<SectionGuard section="security"><Security /></SectionGuard>} />
        <Route path="backup" element={<SectionGuard section="backup"><Backup /></SectionGuard>} />
        <Route path="settings" element={<SectionGuard section="settings"><Settings /></SectionGuard>} />
        <Route path="pc-production" element={<PCProduction />} />
        <Route path="website-production" element={<WPGuard><WPList /></WPGuard>} />
        <Route path="website-production/new" element={<WPGuard><WPOrderForm /></WPGuard>} />
        <Route path="website-production/presets" element={<WPGuard><WPPresets /></WPGuard>} />
        <Route path="website-production/:id" element={<WPGuard><WPOrderDetail /></WPGuard>} />
        <Route path="website-production/:id/edit" element={<WPGuard><WPOrderForm /></WPGuard>} />
        <Route path="website-production/:id/package" element={<WPGuard><WPClientPackage /></WPGuard>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      <Route path="/admin/cockpit/:clientId" element={<Protected roles={["super_admin", "team_member"]} loginPath="/admin/login"><CockpitLayout /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="website" element={<CWebsite />} />
        <Route path="leads" element={<CLeads />} />
        <Route path="tasks" element={<CTasks />} />
        <Route path="support" element={<CSupport />} />
        <Route path="performance" element={<CPerformance />} />
        <Route path="invoices" element={<CDocsInvoices />} />
        <Route path="documents" element={<CFiles />} />
        <Route path="freigaben" element={<CFiles />} />
        <Route path="next-steps" element={<CNextSteps />} />
        <Route path="reports" element={<CReports />} />
        <Route path="reviews" element={<CReviews />} />
        <Route path="design" element={<CDesign />} />
        <Route path="requests" element={<CRequests />} />
        <Route path="team" element={<CTeam />} />
        <Route path="security" element={<CSecurity />} />
        <Route path="*" element={<AdminCockpitHome />} />
      </Route>

      <Route path="/cockpit" element={<Protected roles={CLIENT_ROLES} loginPath="/login"><CockpitLayout /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="website" element={<CWebsite />} />
        <Route path="leads" element={<CLeads />} />
        <Route path="tasks" element={<CTasks />} />
        <Route path="support" element={<CSupport />} />
        <Route path="performance" element={<CPerformance />} />
        <Route path="invoices" element={<CDocsInvoices />} />
        <Route path="documents" element={<CFiles />} />
        <Route path="freigaben" element={<CFiles />} />
        <Route path="next-steps" element={<CNextSteps />} />
        <Route path="reports" element={<CReports />} />
        <Route path="reviews" element={<CReviews />} />
        <Route path="design" element={<CDesign />} />
        <Route path="requests" element={<CRequests />} />
        <Route path="team" element={<CTeam />} />
        <Route path="security" element={<CSecurity />} />
        <Route path="*" element={<Navigate to="/cockpit" replace />} />
      </Route>
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <AppErrorBoundary>
            <AppRoutes />
          </AppErrorBoundary>
          <PwaManifestSync />
          <VersionGate />
          <InstallAppButton />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}


